import React, {useEffect, useRef, useState} from 'react';
import {Button, Checkbox, message, Modal, Select, Slider} from 'antd';
import {tradingService} from '../../../services/tradingService';
import {estimateContractQuantity} from '../../../utils/contractQuantity';
import {formatEffectiveDecimal} from '../../../utils/numberFormatter';
import './ChartModalEnhanced.css';

interface QuickTradingPanelProps {
    apiKeyId?: number | null;
    currentPrice?: number;
    priceChange?: number;
    instId?: string;
    fourHourAvgChange?: number; // 4小时平均涨跌幅
    onTradeSuccess?: () => void;
    readOnly?: boolean; // 是否只读模式（禁用交易操作）
}

interface TradeCalculation {
    contract: string;
    direction: 'long' | 'short' | null;
    allocationPercent: number;
    leverage: number;
    orderType: 'market' | 'limit';
    limitPrice?: number;
    estimatedQuantity?: number;
    estimatedNotional?: number;
    estimatedFee?: number;
    liquidationPrice?: number;
    riskWarning?: string;
}

const QuickTradingPanel: React.FC<QuickTradingPanelProps> = ({
                                                                 apiKeyId,
                                                                 currentPrice,
                                                                 priceChange,
                                                                 instId = 'ETH-USDT-SWAP',
                                                                 fourHourAvgChange,
                                                                 onTradeSuccess,
                                                                 readOnly = false
                                                             }) => {
    // 现货模式: instId 无 -SWAP 后缀即现货交易对(如 BTC-USDT)
    const isSpot = !!instId && !instId.endsWith('-SWAP');

    // 交易状态
    const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
    const [selectedAllocationPercent, setSelectedAllocationPercent] = useState<number>(5);
    const [direction, setDirection] = useState<'long' | 'short' | null>(null);
    const [limitPrice, setLimitPrice] = useState<number>(currentPrice || 0);
    const [leverage, setLeverage] = useState<number>(3); // 固定3倍杠杆
    const [contractSpec, setContractSpec] = useState<{ ctVal?: number; lotSz?: number; minSz?: number }>({});
    // 现货下单数量(币数量)
    const [spotQuantity, setSpotQuantity] = useState<number | null>(null);

    // 价格编辑状态管理
    const [hasManuallyEditedPrice, setHasManuallyEditedPrice] = useState(false);  // 用户是否手动编辑过价格
    const [isUserEditing, setIsUserEditing] = useState(false);  // 用户是否正在编辑
    const [priceFlashClass, setPriceFlashClass] = useState('');  // 价格更新时的闪烁动画class

    // 止盈止损状态（简化版，只支持百分比）
    // 默认勾选，使用默认公式设置百分比
    const defaultTP = parseFloat((0.8 / leverage * 100).toFixed(2)); // 默认止盈: 0.8/杠杆*100
    const defaultSL = parseFloat((0.6 / leverage * 100).toFixed(2)); // 默认止损: 0.6/杠杆*100
    const [takeProfitEnabled, setTakeProfitEnabled] = useState(true);
    const [stopLossEnabled, setStopLossEnabled] = useState(true);
    const [takeProfitPct, setTakeProfitPct] = useState<number | null>(defaultTP);
    const [stopLossPct, setStopLossPct] = useState<number | null>(defaultSL);

    // 实时计算的止盈止损价格
    const [calculatedPrices, setCalculatedPrices] = useState({
        longTakeProfit: null as number | null,
        shortTakeProfit: null as number | null,
        longStopLoss: null as number | null,
        shortStopLoss: null as number | null
    });

    // 预期盈亏金额
    const [expectedAmounts, setExpectedAmounts] = useState({
        longTakeProfitAmount: null as number | null,
        shortTakeProfitAmount: null as number | null,
        longStopLossAmount: null as number | null,
        shortStopLossAmount: null as number | null
    });

    // 预览数据
    // 交易加载状态
    const [isTrading, setIsTrading] = useState(false);

    // 使用ref来跟踪当前值，避免循环依赖
    const takeProfitPctRef = useRef(takeProfitPct);
    const stopLossPctRef = useRef(stopLossPct);
    const prevInstIdRef = useRef(''); // 跟踪上一次的合约ID，初始为空确保首次渲染触发

    // 更新ref值
    useEffect(() => {
        takeProfitPctRef.current = takeProfitPct;
    }, [takeProfitPct]);

    useEffect(() => {
        stopLossPctRef.current = stopLossPct;
    }, [stopLossPct]);

    // 计算结果
    const [calculation, setCalculation] = useState<TradeCalculation>({
        contract: instId,
        direction: null,
        allocationPercent: selectedAllocationPercent,
        leverage: leverage,
        orderType: 'market',
        riskWarning: '实盘风险较高，请谨慎操作'
    });

    // 杠杆选项
    const leverageOptions = [
        {value: 2, label: '2x'},
        {value: 3, label: '3x'},
        {value: 5, label: '5x'}
    ];

    // 合并止盈止损状态管理：合约切换使用默认公式 + 接口返回后更新智能值
    useEffect(() => {
        const instIdChanged = prevInstIdRef.current !== instId;

        // 现货模式:无杠杆百分比口径,止盈止损默认禁用(快捷面板聚焦快速买卖,止盈止损走完整表单)
        if (isSpot) {
            setTakeProfitEnabled(false);
            setStopLossEnabled(false);
            // 切换现货交易对时清除数量
            if (instIdChanged) {
                setSpotQuantity(null);
            }
            prevInstIdRef.current = instId;
            return;
        }

        // 计算默认值
        const defaultTP = parseFloat((0.8 / leverage * 100).toFixed(2));
        const defaultSL = parseFloat((0.6 / leverage * 100).toFixed(2));

        // 合约切换或首次渲染时设置默认值
        if (instIdChanged) {
            setTakeProfitEnabled(true);
            setStopLossEnabled(true);
            setTakeProfitPct(defaultTP);
            setStopLossPct(defaultSL);
        }

        prevInstIdRef.current = instId;

        // 当 fourHourAvgChange 有效时，更新为智能值
        if (fourHourAvgChange && fourHourAvgChange !== 0) {
            const absChange = Math.abs(fourHourAvgChange);
            const takeProfitSmart = parseFloat((absChange * 1.2).toFixed(2));
            const stopLossSmart = parseFloat((absChange * 1.5).toFixed(2));

            setTakeProfitPct(takeProfitSmart);
            setStopLossPct(stopLossSmart);
        }
    }, [instId, fourHourAvgChange, leverage, isSpot]);

    useEffect(() => {
        const loadContractSpec = async () => {
            if (!instId || !apiKeyId) {
                setContractSpec({});
                return;
            }
            try {
                const overview = await tradingService.getInstrumentOverview(instId, apiKeyId, ['5m', '4H', '1D']);
                setContractSpec({
                    ctVal: overview.ctVal,
                    lotSz: overview.lotSz,
                    minSz: overview.minSz
                });
            } catch (error) {
                setContractSpec({});
            }
        };

        loadContractSpec();
    }, [instId, apiKeyId]);

    // 更新价格变化（仅在用户未手动编辑时）
    useEffect(() => {
        if (currentPrice && orderType === 'limit' && !hasManuallyEditedPrice && !isUserEditing) {
            const previousPrice = limitPrice;
            setLimitPrice(currentPrice);

            // 添加价格更新视觉反馈（闪烁效果）
            if (previousPrice && previousPrice !== currentPrice) {
                setPriceFlashClass(currentPrice > previousPrice ? 'price-flash-up' : 'price-flash-down');
                setTimeout(() => setPriceFlashClass(''), 300);
            }
        }
    }, [currentPrice, orderType, hasManuallyEditedPrice, isUserEditing]);

    // 调用预览 API 获取真实可用保证金估算（必须在引用它的useEffect之前声明）
    const [previewMargin, setPreviewMargin] = useState<number | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const previewAvailable = previewMargin !== null && !previewLoading;

    // 实时计算止盈止损价格和预期金额
    useEffect(() => {
        if (!currentPrice) return;

        const newPrices = {
            longTakeProfit: takeProfitEnabled && takeProfitPct && takeProfitPct > 0
                ? currentPrice * (1 + takeProfitPct / 100)
                : null,
            shortTakeProfit: takeProfitEnabled && takeProfitPct && takeProfitPct > 0
                ? currentPrice * (1 - takeProfitPct / 100)
                : null,
            longStopLoss: stopLossEnabled && stopLossPct && stopLossPct > 0
                ? currentPrice * (1 - stopLossPct / 100)
                : null,
            shortStopLoss: stopLossEnabled && stopLossPct && stopLossPct > 0
                ? currentPrice * (1 + stopLossPct / 100)
                : null
        };

        // 计算预期盈亏金额（基于预览返回的真实保证金，无预览时跳过）
        const estimatedMargin = previewMargin || 0;
        const totalValue = estimatedMargin * leverage;
        const newAmounts = {
            longTakeProfitAmount: takeProfitEnabled && takeProfitPct ?
                totalValue * (takeProfitPct / 100) : null,
            shortTakeProfitAmount: takeProfitEnabled && takeProfitPct ?
                totalValue * (takeProfitPct / 100) : null,
            longStopLossAmount: stopLossEnabled && stopLossPct ?
                totalValue * (stopLossPct / 100) : null,
            shortStopLossAmount: stopLossEnabled && stopLossPct ?
                totalValue * (stopLossPct / 100) : null
        };

        setCalculatedPrices(newPrices);
        setExpectedAmounts(newAmounts);
    }, [currentPrice, takeProfitEnabled, stopLossEnabled, takeProfitPct, stopLossPct, selectedAllocationPercent, leverage, previewMargin]);

    useEffect(() => {
        // 现货模式无需保证金预览(不经 sizing 链路)
        if (isSpot) {
            setPreviewMargin(null);
            return;
        }
        if (!apiKeyId || !instId || !selectedAllocationPercent || selectedAllocationPercent <= 0) {
            setPreviewMargin(null);
            return;
        }
        let cancelled = false;
        setPreviewLoading(true);
        tradingService.previewOrderSizing({
            apiKeyId,
            instId,
            allocationPercent: selectedAllocationPercent,
            side: 'buy',
            orderType,
            lever: leverage,
            px: orderType === 'limit' ? limitPrice : undefined,
            source: 'web'
        }).then((resp: any) => {
            if (!cancelled && resp?.data?.success && resp.data.data) {
                const d = resp.data.data;
                setPreviewMargin(d.actualMargin || d.requestedMargin || null);
            }
            setPreviewLoading(false);
        }).catch(() => {
            if (!cancelled) setPreviewMargin(null);
            setPreviewLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [apiKeyId, instId, selectedAllocationPercent, orderType, leverage, limitPrice]);

    // 实时计算交易信息
    useEffect(() => {
        calculateTradeInfo();
    }, [direction, selectedAllocationPercent, orderType, limitPrice, currentPrice, takeProfitEnabled, stopLossEnabled, takeProfitPct, stopLossPct, leverage, contractSpec, previewMargin]);

    // 计算交易信息
    const calculateTradeInfo = () => {
        if (!direction || !currentPrice || !previewAvailable) return;

        const price = orderType === 'market' ? currentPrice : limitPrice;
        const estimatedMargin = previewMargin!;
        const estimatedQuantity = estimateContractQuantity({
            amount: estimatedMargin,
            leverage,
            price,
            ctVal: contractSpec.ctVal,
            lotSz: contractSpec.lotSz,
            minSz: contractSpec.minSz
        });
        const estimatedNotional = estimatedMargin * leverage;
        const estimatedFee = estimatedNotional * 0.0005;
        const liquidationPrice = calculateLiquidationPrice(price, direction, leverage);

        setCalculation({
            contract: instId,
            direction,
            allocationPercent: selectedAllocationPercent,
            leverage: leverage,
            orderType,
            limitPrice: orderType === 'limit' ? limitPrice : undefined,
            estimatedQuantity,
            estimatedNotional,
            estimatedFee,
            liquidationPrice,
            riskWarning: selectedAllocationPercent >= 25 ? '高比例交易风险高，请谨慎操作' : '实盘交易风险高，请谨慎操作'
        });
    };

    // 计算强平价格
    const calculateLiquidationPrice = (price: number, dir: 'long' | 'short', leverage: number): number => {
        const maintenanceMarginRate = 0.005; // 维持保证金率
        if (dir === 'long') {
            return price * (1 - 1 / leverage + maintenanceMarginRate);
        } else {
            return price * (1 + 1 / leverage - maintenanceMarginRate);
        }
    };


    // 现货快捷下单: 数量校验+lotSz校验后直传(sz),无杠杆/比例语义
    const handleSpotTrade = async (side: 'buy' | 'sell') => {
        // 现货数量校验
        if (!spotQuantity || spotQuantity <= 0) {
            message.error('请输入大于0的现货数量');
            return;
        }
        // 最小下单量校验(lotSz)
        const minQty = contractSpec.lotSz || contractSpec.minSz;
        if (minQty && spotQuantity < minQty) {
            message.error(`低于最小下单量 ${formatEffectiveDecimal(minQty, 2, 0, 8)}，请调整数量`);
            return;
        }
        // 限价单价格校验
        if (orderType === 'limit' && (!limitPrice || limitPrice <= 0)) {
            message.error('限价单必须设置有效价格');
            return;
        }

        setIsTrading(true);
        setDirection(side === 'buy' ? 'long' : 'short'); // 按钮加载态复用 direction

        try {
            const orderRequest = {
                apiKeyId: apiKeyId!,
                instId,
                instType: 'SPOT',
                side,
                orderType,
                sz: spotQuantity,
                px: orderType === 'limit' ? limitPrice : undefined,
                lever: 1, // 现货无杠杆,占位值(后端SPOT分支忽略)
                source: 'web' as const
            };

            const response = await tradingService.placeOrder(orderRequest);
            const result = response.data;

            if (result.success) {
                const orderId = result.data?.orderId || (result as any).orderId;
                message.success({
                    content: `${side === 'buy' ? '买入' : '卖出'}订单提交成功！${orderId ? `订单ID: ${orderId}` : ''}`,
                    duration: 5,
                    style: {zIndex: 9999}
                });
                onTradeSuccess?.();
            } else {
                message.error({
                    content: `订单提交失败: ${result.message || '未知错误'}`,
                    duration: 5,
                    style: {zIndex: 9999}
                });
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                message.error({
                    content: '认证失败，请检查API密钥配置',
                    duration: 5,
                    style: {zIndex: 9999}
                });
            } else if (error.response?.status === 403) {
                message.error({
                    content: '权限不足，请检查API密钥权限',
                    duration: 5,
                    style: {zIndex: 9999}
                });
            } else {
                message.error({
                    content: `网络错误: ${error.message || '请稍后重试'}`,
                    duration: 5,
                    style: {zIndex: 9999}
                });
            }
        } finally {
            setIsTrading(false);
        }
    };

    // 处理真实交易
    const handleTrade = async (tradeDirection: 'long' | 'short') => {
        if (!apiKeyId) {
            message.error('请先选择 API Key');
            return;
        }

        if (!instId) {
            message.error('交易参数不完整');
            return;
        }

        // 现货快捷下单: 按币数量直传(sz),无杠杆/比例语义
        if (isSpot) {
            await handleSpotTrade(tradeDirection === 'long' ? 'buy' : 'sell');
            return;
        }

        // 合约模式需要标记价格计算预览
        if (!currentPrice) {
            message.error('交易参数不完整');
            return;
        }

        if (!previewAvailable) {
            message.error('正在计算可用保证金，请稍后重试');
            return;
        }

        setIsTrading(true);
        setDirection(tradeDirection);

        try {
            // 构建交易请求
            const takeProfitPx = takeProfitEnabled
                ? calculatedPrices[tradeDirection === 'long' ? 'longTakeProfit' : 'shortTakeProfit']
                : undefined;
            const stopLossPx = stopLossEnabled
                ? calculatedPrices[tradeDirection === 'long' ? 'longStopLoss' : 'shortStopLoss']
                : undefined;

            const orderRequest = {
                apiKeyId,
                instId,
                side: (tradeDirection === 'long' ? 'buy' : 'sell') as 'buy' | 'sell',
                orderType,
                allocationPercent: selectedAllocationPercent,
                lever: leverage,
                px: orderType === 'limit' ? limitPrice : undefined,
                takeProfitPrice: takeProfitPx === null ? undefined : takeProfitPx,
                stopLossPrice: stopLossPx === null ? undefined : stopLossPx,
                posSide: tradeDirection,
                source: 'web' as const
            };

            // console.log('执行交易:', orderRequest);

            // 调用真实交易接口
            const response = await tradingService.placeOrder(orderRequest);
            // console.log('交易响应:', response);

            const result = response.data;
            // console.log('交易结果:', result);

            if (result.success) {
                // 检查风控模式
                const isManualRiskMode = result.data?.isManualRiskMode || false;
                const orderId = result.data?.orderId || (result as any).orderId;

                if (isManualRiskMode) {
                    // 风控审核模式 - 使用Modal显示
                    Modal.success({
                        title: <span style={{color: '#ffffff'}}>
                            进入风控审核
                        </span>,
                        content: (
                            <div style={{color: '#a0a0a0'}}>
                                <div>订单已进入风控审核流程，请等待风控审核</div>
                                {orderId && (
                                    <div style={{marginTop: '8px'}}>订单ID: {orderId}</div>
                                )}
                            </div>
                        ),
                        centered: true,
                        className: 'dark-theme-modal'
                    });
                } else {
                    // 自动通过模式 - 使用Message显示
                    message.success({
                        content: `${tradeDirection === 'long' ? '开多' : '开空'}订单提交成功！${orderId ? `订单ID: ${orderId}` : ''}`,
                        duration: 5,
                        style: {
                            zIndex: 9999
                        }
                    });
                }

                onTradeSuccess?.();
            } else {
                message.error({
                    content: `订单提交失败: ${result.message || '未知错误'}`,
                    duration: 5,
                    style: {
                        zIndex: 9999
                    }
                });
            }
        } catch (error: any) {
            // console.error('交易执行失败:', error);

            // 根据错误类型显示不同的提示
            if (error.response?.status === 401) {
                message.error({
                    content: '认证失败，请检查API密钥配置',
                    duration: 5,
                    style: {
                        zIndex: 9999
                    }
                });
            } else if (error.response?.status === 403) {
                message.error({
                    content: '权限不足，请检查API密钥权限',
                    duration: 5,
                    style: {
                        zIndex: 9999
                    }
                });
            } else {
                message.error({
                    content: `网络错误: ${error.message || '请稍后重试'}`,
                    duration: 5,
                    style: {
                        zIndex: 9999
                    }
                });
            }
        } finally {
            setIsTrading(false);
        }
    };

    return (
        <div className="quick-trading-area">
            {/* 6行信息展示面板 - 纯信息显示 */}
            <div className="info-display-section">
                {/* 第1行：订单类型 */}
                <div className="info-row">
                    <div className="info-label">订单类型</div>
                    <div className="info-value">
                        {orderType === 'market' ? '市价单' : '限价单'}
                    </div>
                </div>

                {/* 第2行：委托价格 */}
                <div className="info-row">
                    <div className="info-label">委托价格</div>
                    <div className="info-value">
                        {orderType === 'limit' ? `${limitPrice.toFixed(2)}` : '市价'}
                    </div>
                </div>

                {/* 第3行：开仓比例(合约) / 现货数量(现货) */}
                <div className="info-row">
                    <div className="info-label">{isSpot ? '现货数量' : '开仓比例'}</div>
                    <div className="info-value">
                        {isSpot
                            ? (spotQuantity
                                ? formatEffectiveDecimal(spotQuantity, 2, 0, 8)
                                : '--')
                            : `${selectedAllocationPercent}%`
                        }
                    </div>
                </div>

                {/* 第4行：杠杆(仅合约) */}
                {!isSpot && (
                    <div className="info-row">
                        <div className="info-label">杠杆</div>
                        <div className="info-value">
                            {leverage}x
                        </div>
                    </div>
                )}

                <div className="info-row">
                    <div className="info-label">{isSpot ? '预估金额' : '预估张数'}</div>
                    <div className="info-value">
                        {isSpot
                            ? (spotQuantity && currentPrice
                                ? `$${formatEffectiveDecimal(spotQuantity * currentPrice, 2, 0, 2)}`
                                : '--')
                            : (calculation.estimatedQuantity
                                ? formatEffectiveDecimal(calculation.estimatedQuantity, 2, 0, 8)
                                : '--')
                        }
                    </div>
                </div>

                {/* 第5行：止盈价格（左右两列,仅合约） */}
                {!isSpot && (
                    <div className="info-row two-columns">
                        <div className="info-column">
                            <div className="info-label">开多止盈</div>
                            <div className="info-value positive">
                                {calculatedPrices.longTakeProfit && expectedAmounts.longTakeProfitAmount
                                    ? (
                                        <div>
                                            <div
                                                className="price-line">${calculatedPrices.longTakeProfit.toFixed(2)}</div>
                                            <div
                                                className="amount-line">+${expectedAmounts.longTakeProfitAmount.toFixed(2)}</div>
                                        </div>
                                    )
                                    : '--'
                                }
                            </div>
                        </div>
                        <div className="info-column">
                            <div className="info-label">开空止盈</div>
                            <div className="info-value positive">
                                {calculatedPrices.shortTakeProfit && expectedAmounts.shortTakeProfitAmount
                                    ? (
                                        <div>
                                            <div
                                                className="price-line">${calculatedPrices.shortTakeProfit.toFixed(2)}</div>
                                            <div
                                                className="amount-line">+${expectedAmounts.shortTakeProfitAmount.toFixed(2)}</div>
                                        </div>
                                    )
                                    : '--'
                                }
                            </div>
                        </div>
                    </div>
                )}

                {/* 第6行：止损价格（左右两列,仅合约） */}
                {!isSpot && (
                    <div className="info-row two-columns">
                        <div className="info-column">
                            <div className="info-label">开多止损</div>
                            <div className="info-value negative">
                                {calculatedPrices.longStopLoss && expectedAmounts.longStopLossAmount
                                    ? (
                                        <div>
                                            <div
                                                className="price-line">${calculatedPrices.longStopLoss.toFixed(2)}</div>
                                            <div
                                                className="amount-line">-${expectedAmounts.longStopLossAmount.toFixed(2)}</div>
                                        </div>
                                    )
                                    : '--'
                                }
                            </div>
                        </div>
                        <div className="info-column">
                            <div className="info-label">开空止损</div>
                            <div className="info-value negative">
                                {calculatedPrices.shortStopLoss && expectedAmounts.shortStopLossAmount
                                    ? (
                                        <div>
                                            <div
                                                className="price-line">${calculatedPrices.shortStopLoss.toFixed(2)}</div>
                                            <div
                                                className="amount-line">-${expectedAmounts.shortStopLossAmount.toFixed(2)}</div>
                                        </div>
                                    )
                                    : '--'
                                }
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 交易控制面板 - 保持原有结构 */}
            <div className="trading-controls-section">
                {/* 第1行：订单类型选择 */}
                <div className="control-row order-type-row">
                    <label className="control-label">类型</label>
                    <div className="control-content">
                        <div className="order-type-selector">
                            <button
                                className={orderType === 'market' ? 'active' : ''}
                                onClick={() => {
                                    setOrderType('market');
                                    setHasManuallyEditedPrice(false);  // 重置编辑状态
                                    setIsUserEditing(false);
                                }}
                            >
                                市价单
                            </button>
                            <button
                                className={orderType === 'limit' ? 'active' : ''}
                                onClick={() => {
                                    setOrderType('limit');
                                    setHasManuallyEditedPrice(false);  // 重置编辑状态
                                    setIsUserEditing(false);
                                }}
                            >
                                限价单
                            </button>
                        </div>
                    </div>
                </div>

                {/* 第2行：委托价格 (仅限价单显示) */}
                {orderType === 'limit' && (
                    <div className="control-row limit-price-row">
                        <label className="control-label">委托价</label>
                        <div className="control-content" style={{position: 'relative'}}>
                            <input
                                type="number"
                                value={limitPrice}
                                onChange={(e) => {
                                    setLimitPrice(Number(e.target.value));
                                    setHasManuallyEditedPrice(true);  // 标记用户已手动编辑
                                }}
                                onFocus={() => setIsUserEditing(true)}
                                onBlur={(e) => {
                                    setIsUserEditing(false);
                                    // 失焦时格式化显示
                                    const numValue = Number(e.target.value);
                                    if (!isNaN(numValue) && numValue > 0) {
                                        const formatted = formatEffectiveDecimal(numValue, 2, 0, 4);
                                        setLimitPrice(parseFloat(formatted));
                                    }
                                }}
                                className={`limit-price-input ${priceFlashClass}`}
                                placeholder="输入限价"
                                step="0.0001"
                                style={{textAlign: 'right'}}
                            />
                            {hasManuallyEditedPrice && currentPrice && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLimitPrice(currentPrice);
                                        setHasManuallyEditedPrice(false);
                                    }}
                                    style={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'var(--pt-primary)',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        padding: '0 4px'
                                    }}
                                >
                                    恢复市价
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* 第3行：开仓比例选择(合约) / 现货数量输入(现货) */}
                <div className="control-row amount-row">
                    <label className="control-label">{isSpot ? '数量' : '比例'}</label>
                    <div className="control-content">
                        {isSpot ? (
                            <div className="spot-quantity-input">
                                <input
                                    type="number"
                                    value={spotQuantity ?? ''}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        setSpotQuantity(value > 0 ? value : null);
                                    }}
                                    placeholder="输入币数量"
                                    step={contractSpec.lotSz || 0.00001}
                                    min="0"
                                />
                                {/* 一键按最小量: 数量低于lotSz时提示 */}
                                {spotQuantity !== null && spotQuantity > 0
                                    && (contractSpec.lotSz || contractSpec.minSz)
                                    && spotQuantity < ((contractSpec.lotSz || contractSpec.minSz) as number) && (
                                        <button
                                            type="button"
                                            className="spot-min-fill-btn"
                                            onClick={() => setSpotQuantity((contractSpec.lotSz || contractSpec.minSz) as number)}
                                        >
                                            一键按最小量
                                        </button>
                                    )}
                            </div>
                        ) : (
                            <div className="amount-selector">
                                <Slider
                                    min={0.01}
                                    max={25}
                                    step={0.1}
                                    value={selectedAllocationPercent}
                                    onChange={setSelectedAllocationPercent}
                                    tooltip={{formatter: (v) => `${v}%`}}
                                />
                                <span className="amount-selector-value">
                                    {formatEffectiveDecimal(selectedAllocationPercent, 1, 0, 2)}%
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* 第4行：杠杆选择器(仅合约) */}
                {!isSpot && (
                    <div className="control-row leverage-row">
                        <label className="control-label">杠杆</label>
                        <div className="control-content">
                            <Select
                                value={leverage}
                                onChange={(value) => setLeverage(value)}
                                size="small"
                                style={{width: '100%'}}
                                options={leverageOptions}
                                disabled
                            />
                        </div>
                    </div>
                )}

                {/* 第5行：止盈设置(仅合约,现货止盈止损在完整表单设置) */}
                {!isSpot && (
                    <div className="control-row take-profit-row">
                        <label className="control-label">止盈</label>
                        <div className="control-content">
                            <div className="profit-loss-control" data-enabled={takeProfitEnabled}>
                                <div className="profit-loss-header">
                                    <Checkbox
                                        checked={takeProfitEnabled}
                                        onChange={(e) => setTakeProfitEnabled(e.target.checked)}
                                    >
                                    </Checkbox>
                                </div>
                                {takeProfitEnabled && (
                                    <div className="profit-loss-input">
                                        {/* DEBUG: takeProfitPct={takeProfitPct}, stopLossPct={stopLossPct} */}
                                        <div className="percentage-input-wrapper">
                                            <input
                                                type="number"
                                                value={takeProfitPct ?? ''}
                                                onChange={(e) => {
                                                    const value = Number(e.target.value);
                                                    setTakeProfitPct(value > 0 ? value : null);
                                                }}
                                                placeholder="止盈百分比"
                                                step="0.01"
                                                min="0.01"
                                            />
                                            <span className="percentage-sign">%</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* 第6行：止损设置(仅合约) */}
                {!isSpot && (
                    <div className="control-row stop-loss-row">
                        <label className="control-label">止损</label>
                        <div className="control-content">
                            <div className="profit-loss-control" data-enabled={stopLossEnabled}>
                                <div className="profit-loss-header">
                                    <Checkbox
                                        checked={stopLossEnabled}
                                        onChange={(e) => setStopLossEnabled(e.target.checked)}
                                    >
                                    </Checkbox>
                                </div>
                                {stopLossEnabled && (
                                    <div className="profit-loss-input">
                                        <div className="percentage-input-wrapper">
                                            <input
                                                type="number"
                                                value={stopLossPct ?? ''}
                                                onChange={(e) => {
                                                    const value = Number(e.target.value);
                                                    setStopLossPct(value > 0 ? value : null);
                                                }}
                                                placeholder="止损百分比"
                                                step="0.01"
                                                min="0.01"
                                            />
                                            <span className="percentage-sign">%</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* 交易按钮 */}
                <div className="trading-buttons">
                    <Button
                        type="primary"
                        className={direction === 'long' ? 'buy-button selected' : 'buy-button'}
                        loading={isTrading && direction === 'long'}
                        onClick={() => handleTrade('long')}
                        disabled={readOnly}
                        style={readOnly ? {opacity: 0.35} : undefined}
                    >
                        {isTrading && direction === 'long' ? '交易中...' : (isSpot ? '买入' : '开多')}
                    </Button>
                    <Button
                        type="primary"
                        className={direction === 'short' ? 'sell-button selected' : 'sell-button'}
                        loading={isTrading && direction === 'short'}
                        onClick={() => handleTrade('short')}
                        danger
                        disabled={readOnly}
                        style={readOnly ? {opacity: 0.35} : undefined}
                    >
                        {isTrading && direction === 'short' ? '交易中...' : (isSpot ? '卖出' : '开空')}
                    </Button>
                </div>

                {/* 风险提示 */}
                <div className="risk-warning">
                    ⚠️ {isSpot ? '现货交易请确认数量与价格，实盘资金将真实投入市场' : calculation.riskWarning}
                </div>
            </div>
        </div>
    );
};

export default QuickTradingPanel;
